import { useEffect, useMemo, useRef, useState } from 'react';
import useLocales from 'src/hooks/useLocales';

// @mui
import {
  Box,
  Card,
  Table,
  Button,
  TableBody,
  Container,
  TableContainer,
  Grid,
  Stack,
  TableHead,
  TableRow,
  Paper,
  TextField,
  InputAdornment,
  TableCell,
  Autocomplete,
  Chip,
  IconButton,
  useTheme,
} from '@mui/material';
import * as XLSX from 'xlsx';

import { observer } from 'mobx-react-lite';

import HeaderBreadcrumbs from 'src/components/HeaderBreadcrumbs';
import Iconify from 'src/components/Iconify';

import useSettings from 'src/hooks/useSettings';

import { PATH_DASHBOARD } from 'src/routes/paths';
import Page from 'src/components/Page';
import { useStore } from 'src/stores/store';
import { Controller, useForm } from 'react-hook-form';
import LocalizDatePicker from 'src/sections/common/LocalizDatePicker';
import Loader from 'src/components/loader/Loader';

import { GetMonthName } from 'src/sections/common/GetMonthName';
import { DateConverter } from 'src/sections/common/DateConverter';
import Label from 'src/components/Label';
import ShopReportDetailsToolbar from './ShopReportDetailsToolbar';
import { IShopReportParam, ShopReportEntry } from 'src/@types/Choqorak/MainTables/Shop/Shop';
import { Icon } from '@iconify/react';
// ----------------------------------------------------------------------
export default observer(function ShopReportIndex() {
  const theme = useTheme();
  const themeMode = () => theme.palette.mode;
  const { ShopStore, ChoqorakCommonDropdown } = useStore();
  const { translate } = useLocales();
  const { loadShopReport, ShopReportResult } = ShopStore;
  const {
    loadBlockeDDL,
    BlockOption,
    loadFloorDDL,
    FloorOption,
    loadShopTypeDDL,
    ShopTypeOption,
    loadStatusDDL,
    StatusOption,
  } = ChoqorakCommonDropdown;

  const { themeStretch } = useSettings();
  const [isloading, setIsloading] = useState(false);
  const [floor, setFloor] = useState();
  const [block, setBlock] = useState();
  const [status, setStatus] = useState();
  const [shopType, setShoptype] = useState();
  const blockoptions = [{ value: undefined, text: '' }, ...BlockOption];
  const flooroptions = [{ value: undefined, text: '' }, ...FloorOption];
  const shopTypeoptions = [{ value: undefined, text: '' }, ...ShopTypeOption];
  const statusoptions = [{ value: undefined, text: '' }, ...StatusOption];
  const defaultValues = useMemo<IShopReportParam>(
    () => ({
      floorId: undefined,
      blockId: undefined,
      shopStatusId: undefined,
      shopTypeId: undefined,
    }),
    []
  );
  const methods = useForm<IShopReportParam>({
    defaultValues,
  });
  const { watch, control, setValue } = methods;
  const val = watch();

  useEffect(() => {
    loadBlockeDDL();
    loadFloorDDL();
    loadShopTypeDDL();
    loadStatusDDL(0);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const Search = async (e: any) => {
    e.preventDefault();
    setIsloading(true);
    loadShopReport({
      floorId: floor,
      blockId: block,
      shopStatusId: status,
      shopTypeId: shopType,
    })
      .then((res) => {
        setIsloading(false);
      })
      .catch((err) => {
        setIsloading(false);
      });
  };

  const ResetForm = () => {
    setFloor(undefined);
    setBlock(undefined);
    setStatus(undefined);
    setShoptype(undefined);
    methods.reset({
      floorId: undefined,
      blockId: undefined,
      shopStatusId: undefined,
      shopTypeId: undefined,
    });
  };

  // useEffect(() => {}, [floor, block, status, shopType]);

  const exportToExcel = () => {
    const data =
      ShopReportResult?.list?.map((row, index) => ({
        No: index + 1,
        Block: row.blockName,
        Floor: row.floorCode,
        ShopCode: row.shopCode,
        RentPerMonth: row.rentPerMonth,
        ShopType: row.shopType,
        Status: row.shopStatus,
      })) || []; // Fallback to an empty array if undefined

    if (data.length === 0) {
      alert('No data available to export.');
      return;
    }

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Shop Report');

    XLSX.writeFile(workbook, 'Shop_Report.xlsx');
  };
  const autocompleteRef = useRef<any>(null);
  // Function to clear selection

  interface GroupedReport {
    shopTypeName: string;
    statuses: ShopReportEntry[];
  }

  const groupedData: GroupedReport[] =
    ShopReportResult?.report.reduce<GroupedReport[]>((acc, item) => {
      const existingGroup = acc.find(
        (group) => group.shopTypeName === item.shopTypeName
      );

      if (existingGroup) {
        existingGroup.statuses.push(item);
      } else {
        acc.push({
          shopTypeName: item.shopTypeName,
          statuses: [item],
        });
      }
      return acc;
    }, []) ?? [];
  return (
    <Page title={translate('ShopReport.Title')}>
      <Container maxWidth={themeStretch ? false : 'lg'}>
        <HeaderBreadcrumbs
          heading={translate('ShopReport.Report')}
          links={[
            { name: `${translate('Department.Dashboard')}`, href: PATH_DASHBOARD.root },

            { name: `${translate('ShopReport.Report')}` },
          ]}
          action={<></>}
        />

        <Grid
          container
          display="flex"
          alignItems="center"
          justifyContent="center" // Use justifyContent instead of justifyItems
        >
          <Grid item xs={12} md={12} spacing={2}>
            <Card
              sx={{
                p: 3,
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'center', // Use justifyContent instead of justifyItems
              }}
            >
              <Grid item xs={12} md={12}>
                <Box
                  sx={{
                    display: 'grid',
                    columnGap: 2,
                    rowGap: 3,
                    gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(6, 1fr)' },
                  }}
                >
                  <Controller
                    name="blockId"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <Autocomplete
                        fullWidth
                        freeSolo
                        options={blockoptions.map((option: any) => option)}
                        value={blockoptions.find((opt) => opt.value === field.value) || null} // Ensure correct binding
                        onChange={(event, newValue) => {
                          field.onChange(newValue ? newValue : '');
                          setBlock(newValue ? newValue.value : '');
                        }}
                        getOptionLabel={(option) => option.text || ''}
                        renderTags={(value, getTagProps) =>
                          value.map((option, index) => (
                            <Chip
                              {...getTagProps({ index })}
                              key={option.value}
                              size="small"
                              label={option.text}
                            />
                          ))
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={translate('ShopReport.Block')}
                            error={!!error}
                            size="small"
                            helperText={error?.message}
                          />
                        )}
                        ref={autocompleteRef}
                      />
                    )}
                  />

                  <Controller
                    name="floorId"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <Autocomplete
                        fullWidth
                        freeSolo
                        onChange={(event, newValue) => {
                          field.onChange(newValue ? newValue : '');
                          setFloor(newValue ? newValue.value : '');
                        }}
                        options={flooroptions.map((option: any) => option)}
                        value={flooroptions.find((opt) => opt.value === field.value) || null}
                        getOptionLabel={(option) => option.text}
                        renderTags={(value, getTagProps) =>
                          value.map((option, index) => (
                            <Chip
                              {...getTagProps({ index })}
                              key={option.value}
                              size="small"
                              label={option.value}
                            />
                          ))
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={translate('ShopReport.Floor')}
                            error={!!error}
                            size="small"
                            helperText={error?.message}
                          />
                        )}
                      />
                    )}
                  />

                  <Controller
                    name="shopTypeId"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <Autocomplete
                        fullWidth
                        freeSolo
                        onChange={(event, newValue) => {
                          field.onChange(newValue ? newValue : '');
                          setShoptype(newValue ? newValue.value : '');
                        }}
                        options={shopTypeoptions.map((option: any) => option)}
                        value={shopTypeoptions.find((opt) => opt.value === field.value) || null}
                        getOptionLabel={(option) => option.text}
                        renderTags={(value, getTagProps) =>
                          value.map((option, index) => (
                            <Chip
                              {...getTagProps({ index })}
                              key={option.value}
                              size="small"
                              label={option.value}
                            />
                          ))
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={translate('ShopReport.ShopType')}
                            error={!!error}
                            size="small"
                            helperText={error?.message}
                          />
                        )}
                      />
                    )}
                  />

                  <Controller
                    name="shopStatusId"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <Autocomplete
                        fullWidth
                        freeSolo
                        onChange={(event, newValue) => {
                          field.onChange(newValue ? newValue : '');
                          setStatus(newValue ? newValue.value : '');
                        }}
                        options={statusoptions.map((option: any) => option)}
                        value={statusoptions.find((opt) => opt.value === field.value) || null}
                        getOptionLabel={(option) => option.text}
                        renderTags={(value, getTagProps) =>
                          value.map((option, index) => (
                            <Chip
                              {...getTagProps({ index })}
                              key={option.value}
                              size="small"
                              label={option.value}
                            />
                          ))
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={translate('ShopReport.Status')}
                            error={!!error}
                            size="small"
                            helperText={error?.message}
                          />
                        )}
                      />
                    )}
                  />
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                      sx={{ width: '120px' }}
                      variant="contained"
                      onClick={Search}
                      startIcon={<Iconify icon="eva:search-fill" />}
                    >
                      {translate('ShopReport.Filter')}
                    </Button>

                    <Button
                      sx={{ width: '120px' }}
                      variant="contained"
                      onClick={ResetForm}
                      color="warning"
                      startIcon={<Iconify icon="eva:refresh-outline" />}
                    >
                      {translate('CRUD.Reset')}
                    </Button>
                  </Box>
                </Box>
              </Grid>
              <Grid
                item
                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }} // Use justifyContent instead of justifyItems
              >
                <IconButton
                  sx={{ color: themeMode() === 'light' ? 'black' : 'white' }}
                  onClick={exportToExcel}
                >
                  <Icon icon="vscode-icons:file-type-excel" width={24} height={24} />
                </IconButton>
              </Grid>
              <Grid
                item
                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }} // Use justifyContent instead of justifyItems
              >
                <ShopReportDetailsToolbar Report={ShopReportResult} />
              </Grid>
            </Card>
          </Grid>
        </Grid>

        <Grid item xs={12} md={12} sx={{ mt: 2 }}>
          <>
            {isloading ? (
              <Loader />
            ) : (
              <>
                <div>
                  <h3 className="text-center text-xl font-bold   p-2">
                    {translate('Shop.Report.ReportSummary')}
                  </h3>
                  <table className="table-auto w-full border-collapse border border-black">
                    <thead>
                      <tr className="">
                        <th className="px-4 py-2 font-bold border border-black text-center">
                          {translate('GeneralFields.No')}
                        </th>
                        <th className="px-4 py-2 font-bold border border-black text-center">
                          {translate('Shop.ShopType')}
                        </th>
                        <th className="px-4 py-2 font-bold border border-black text-center">
                          {translate('Shop.ShopStatus')}
                        </th>
                        <th className="px-4 py-2 font-bold border border-black text-center">
                          {translate('Shop.Report.HasContract')}
                        </th>
                        <th className="px-4 py-2 font-bold border border-black text-center">
                          {translate('Shop.Report.HasElectricMeter')}
                        </th>
                        <th className="px-4 py-2 font-bold border border-black text-center">
                          {translate('Shop.Report.WithoutElectricMeter')}
                        </th>
                        <th className="px-4 py-2 font-bold border border-black text-center">
                          {translate('Shop.Report.TotalByStatus')}
                        </th>
                        <th className="px-4 py-2 font-bold border border-black text-center">
                          {translate('Shop.Report.TotalByType')}
                        </th>
                      </tr>
                    </thead>

                    <tbody>
                      {groupedData.map((group, groupIndex) =>
                        group.statuses.map((status, statusIndex) => (
                          <tr key={`${group.shopTypeName}-${status.shopStatusId}`}>
                            <td className="px-4 py-2 border border-black text-center">
                              {groupIndex + 1}.{statusIndex + 1}
                            </td>

                            {statusIndex === 0 && (
                              <td
                                className="px-4 py-2 border border-black text-center"
                                rowSpan={group.statuses.length}
                              >
                                {group.shopTypeName}
                              </td>
                            )}

                            <td className="px-4 py-2 border border-black text-center">
                              {status.shopStatusName}
                            </td>
                            <td className="px-4 py-2 border border-black text-center">
                              {status.shopsWithLastContract}
                            </td>
                            <td className="px-4 py-2 border border-black text-center">
                              {status.shopsWithElectricMeter}
                            </td>
                            <td className="px-4 py-2 border border-black text-center">
                              {status.shopsWithoutElectricMeter}
                            </td>
                            <td className="px-4 py-2 border border-black text-center">
                              {status.totalShopsByStatus}
                            </td>
                            {statusIndex === 0 && (
                              <td rowSpan={group.statuses.length} className="px-4 py-2 border border-black text-center">
                                {status.totalShopsByType}
                              </td>
                            )}
                          </tr>
                        ))
                      )}

                      <tr className=" font-bold">
                        <td className="px-4 py-2 border border-black text-center" colSpan={4}>
                          {translate('Shop.Report.GrandTotal')}
                        </td>
                        <td colSpan={4} className="px-4 py-2 border border-black text-center">
                          {ShopReportResult?.report.reduce((total, item) => item.totalShopsOverall, 0)}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <Stack sx={{ p: 1 }}>
                  <TableContainer sx={{ maxHeight: 500 }} component={Paper}>
                    <Table stickyHeader sx={{ minWidth: 700 }} aria-label="customized table">
                      <TableHead>
                        <TableRow>
                          <TableCell> {translate('ShopReport.No')}</TableCell>
                          <TableCell>{translate('ShopReport.Block')}</TableCell>
                          <TableCell>{translate('ShopReport.Floor')}</TableCell>
                          <TableCell>{translate('ShopReport.ShopCode')}</TableCell>
                          <TableCell>{translate('ShopReport.RentPerMonth')}</TableCell>
                          <TableCell>{translate('ShopReport.ShopType')}</TableCell>
                          <TableCell>{translate('ShopReport.Status')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {ShopReportResult?.list.map((row, index) => (
                          <TableRow key={index}>
                            <TableCell component="th" scope="row">
                              {index + 1}
                            </TableCell>
                            <TableCell align="left">
                              {' '}
                              {row.blockName}
                            </TableCell>
                            <TableCell align="left">
                              {row.floorCode}
                            </TableCell>
                            <TableCell align="left">
                              {row.shopCode}
                            </TableCell>
                            <TableCell align="left">{row.rentPerMonth}</TableCell>
                            <TableCell align="left">
                              {row.shopType}
                            </TableCell>
                            <TableCell align="left" ><Label color={row.shopStatusId === 8 ? "warning" : row.shopStatusId === 7 ? "success" : 'primary'}>{row.shopStatus} </Label></TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Stack>
              </>
            )}
          </>
        </Grid>
      </Container>
    </Page>
  );
});
